import { queries } from "@msuite/zero";
import { useZeroQuery } from "@msuite/zero/react";
import { useAuth } from "@/hooks/use-auth";

export function useDaily(date: string) {
	/** Hooks */
	const { organization, citadel, citadelQuery } = useAuth();

	/** Query */
	const [plan] = useZeroQuery(queries.plans.byDate({ date }), {
		ttl: "forever",
	});

	// The boards of other organisations that carry a crew of ours. Until this query existed a
	// partner organisation could not see its own crew at all: the crew hangs off a plan it does not
	// own, and the board only ever loaded its own plan. The query returns those plans with their
	// crews filtered to ours, so nothing of the host's own planning comes with them.
	const [guestPlans] = useZeroQuery(queries.plans.guestByDate({ date }), {
		ttl: "forever",
	});

	const ownCrews = plan?.crews.filter((crew) => crew.orgId === organization?.id) || [];
	const partnerCrews = plan?.crews.filter((crew) => crew.orgId !== organization?.id) || [];

	const hasCrews = ownCrews.length > 0 || partnerCrews.length > 0;

	// The board's own two rules, asked with the organisation every Plan rule conditions on.
	// Arranging a day is `update Plan` and opening one is `create Plan`; the server asks the same
	// two of every route behind these controls, and the sync path's mutators write the same rows
	// the REST routes do, so the answer has to cover both or the interface offers what the API
	// refuses.
	const canArrangeBoard = citadel?.can("update", "Plan", { orgId: citadel.orgId }) === true;
	const canOpenDay = citadel?.can("create", "Plan", { orgId: citadel.orgId }) === true;
	const isInteractionBlocked = plan?.state === "published" || !canArrangeBoard;

	// Ticket placement is `update Plan` OR the project relationship: holders of the board rule
	// arrange every project's work, and a project's members, their active substitutes and admins
	// arrange their own — `Citadel.canArrangeTickets`, the same rule the sync path's three
	// ticketAssignments mutators and the plan REST handlers enforce. A published day still locks
	// everything, and while the rules load the answer is false, the same fail-closed default
	// `canArrangeBoard` has.
	function canArrangeTicketOf(projectId: string) {
		if (plan?.state === "published") return false;
		return citadel?.canArrangeTickets(projectId) === true;
	}

	// Three states, the way `components/citadel/can.tsx` distinguishes them: a rule list still in
	// flight is not a denial, and a failure to load one is not a denial either. `canArrangeBoard`
	// is false in all three of "no", "not yet" and "could not ask", which is the right default for
	// a control that writes — but it is the wrong thing to *show*, so anything that would otherwise
	// render a plain refusal reads this and renders a load instead. A cached rule list outranks a
	// failed refetch, exactly as it does in `Can`: `citadel` present means answered.
	const rulesState: "loading" | "error" | "ready" = citadel
		? "ready"
		: citadelQuery.isError
			? "error"
			: "loading";

	return {
		plan,
		ownCrews,
		partnerCrews,
		guestPlans,
		isInteractionBlocked,
		hasCrews,
		canArrangeBoard,
		canArrangeTicketOf,
		canOpenDay,
		rulesState,
	};
}

export type DailyPlan = ReturnType<typeof useDaily>["plan"];
export type GuestPlan = NonNullable<ReturnType<typeof useDaily>["guestPlans"]>[number];
export type UseDaily = ReturnType<typeof useDaily>;
